import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { AvatarPicker, FormGrid, NextButton, Notice, OptionAutocomplete, OptionSelect } from '../../../../ui';
import config from '../../../../config';
import noavatar from '../../../../public/images/noavatar.png';
import actions from '../../../../redux/actions';
import { Gender } from '../../../../types/Gender';
import { OperationStatus } from '../../../../types/OperationStatus';
import genderOptions from '../../../../util/options/genders';
import languages from '../../../../util/options/languages';
import userSourceOptions from '../../../../util/options/userSources';

const genderOptionsNoAny = genderOptions.filter((el) => el.key !== Gender.Any);

const mapStateToProps = (state) => {
  return {
    userUploadAvatarStatus: state.userUploadAvatarStatus,
    userSaveStatus: state.userSaveStatus,
    passwordChangeStatus: state.passwordChangeStatus,
    user: state.user,
    isMobile: state.resolution.device === 'mobile' || state.resolution.device === 'mobile-tablet',
    devices: state.devices,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateUser: (data) => dispatch(actions.updateUser(data)),
    saveUser: (data) => dispatch(actions.saveUser(data)),
    uploadAvatar: (avatarFile) => dispatch(actions.uploadAvatar(avatarFile)),
  };
};

class Personal extends Component<any> {
  public state: any;

  constructor(props) {
    super(props);

    this.state = {
      newAvatar: {},
      newAvatarError: '',
      errors: new Set(),
    };
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.userSaveStatus.status !== prevProps.userSaveStatus.status &&
      this.props.userSaveStatus.status === OperationStatus.success
    ) {
      this.props.history.push('/complete-profile/phone');
    }
  }

  resetErrorState = (event) => {
    const name = event.target.name;
    const errors = new Set(this.state.errors);

    if (errors.has(name)) {
      errors.delete(name);
      this.setState({ errors });
    }
  };

  onChange = (event, data) => {
    this.resetErrorState(event);
    const user = { ...this.props.user };

    switch (data.name) {
      case 'age': {
        data.value = data.value.replace(/^[0]+/g, '');
        user[data.name] = data.value;
        break;
      }
      case 'sendInvitations': {
        user[data.name] = data.checked;
        break;
      }
      default: {
        user[data.name] = data.value;
        break;
      }
    }

    return this.props.updateUser(user);
  };

  validateInput = (event, data) => {
    const errors = new Set(this.state.errors);
    const name = data && data.name ? data.name : event.target.name;
    const value = data && data.value ? data.value : event.target.value;

    if (name === 'age') {
      if (!value.length || parseInt(value) < 0 || !Number(value) || parseInt(value) > 120) {
        errors.add(name);
      } else {
        errors.delete(name);
      }

      this.setState({ errors });
      return;
    }

    if (name === 'source') {
      if (!value) {
        errors.add(name);
      } else {
        errors.delete(name);
      }

      this.setState({ errors });
      return;
    }

    if (!value || !value.length) {
      errors.add(name);
    } else {
      errors.delete(name);
    }

    this.setState({ errors });
  };

  onAvatarUpload = ([acceptedImageFile]) => {
    const newAvatar = Object.assign(acceptedImageFile, {
      preview: URL.createObjectURL(acceptedImageFile),
    });

    this.setState({
      newAvatar,
      newAvatarError: '',
    });
  };

  onAvatarRejected = () => {
    this.setState({ newAvatarError: 'Avatar size can be no more than 1 Mb' });
  };

  onSave = async () => {
    const userData = { ...this.props.user };
    const errors = new Set();

    ['firstName', 'lastName', 'gender', 'age', 'nativeLanguage', 'source'].forEach(
      (el) => {
        if (!userData[el]) {
          errors.add(el);
        }

        if (el === 'age') {
          if (userData[el] < 16 || userData[el] > 120) {
            errors.add(el);
          }
        }
      }
    );

    if (errors.size) {
      return this.setState({ errors });
    }

    if (this.state.newAvatar && Object.keys(this.state.newAvatar).length) {
      await this.props.uploadAvatar(this.state.newAvatar);
    }

    this.props.saveUser(userData);
  };

  render() {
    const userSaveProcessing = this.props.userSaveStatus.status === OperationStatus.processing || this.props.userUploadAvatarStatus.status === OperationStatus.processing;
    const user = this.props.user;
    const errors = this.state.errors;

    let avatarUrl;

    if (this.state.newAvatar.preview) {
      avatarUrl = this.state.newAvatar.preview;
    } else if (this.props.user.avatarUrl) {
      const href = new URL(this.props.user.avatarUrl, config.apiEndpoint).href;
      avatarUrl = href;
    } else {
      avatarUrl = noavatar;
    }

    // Adapters to the (event, { name, value }) handlers above
    const change = (name: string, value: any) => this.onChange({ target: { name } }, { name, value });
    const focus = (name: string) => () => this.resetErrorState({ target: { name } });
    const blur = (name: string) => (event?: any) => {
      const value = event?.target?.value ?? user[name] ?? '';
      this.validateInput({ target: { name, value } }, { name, value });
    };
    const text = (name: string, label: string, props: any = {}) => (
      <TextField
        name={name}
        label={label}
        value={user[name] ?? ''}
        error={errors.has(name)}
        onChange={(event) => change(name, event.target.value)}
        onFocus={focus(name)}
        onBlur={blur(name)}
        fullWidth
        {...props}
      />
    );

    return (
      <>
        <Notice title="Welcome!">Please fill the information below to complete your registration</Notice>

        <AvatarPicker
          src={avatarUrl}
          error={this.state.newAvatarError}
          hint="A good profile image makes a client feel more secure"
          onPick={(file) => this.onAvatarUpload([file])}
          onReject={this.onAvatarRejected}
        />

        <FormGrid isMobile={this.props.isMobile}>
          {text('firstName', 'First name', { required: true })}
          {text('lastName', 'Last name', { required: true })}
          <OptionSelect
            name="gender"
            label="Gender"
            required
            options={genderOptionsNoAny}
            value={user.gender}
            error={errors.has('gender')}
            onValueChange={(value) => change('gender', value)}
          />
          {text('age', 'Age', { type: 'number', required: true, inputProps: { min: 16 }, value: user.age || '' })}
          <OptionAutocomplete
            label="Native language"
            required
            options={languages}
            value={user.nativeLanguage}
            error={errors.has('nativeLanguage')}
            onValueChange={(value) => change('nativeLanguage', value)}
            onBlur={blur('nativeLanguage')}
          />
          <OptionAutocomplete label="Other language" options={languages} value={user.otherLanguage} onValueChange={(value) => change('otherLanguage', value)} />
        </FormGrid>

        {text('about', 'About yourself', { multiline: true, minRows: 3 })}

        <FormGrid isMobile={this.props.isMobile}>
          <OptionSelect
            name="source"
            label="How did you reach us?"
            required
            options={userSourceOptions}
            value={user.source}
            error={errors.has('source')}
            onValueChange={(value) => change('source', value)}
          />
          <FormControlLabel
            control={<Switch checked={!!user.sendInvitations} onChange={(event) => this.onChange({ target: { name: 'sendInvitations' } }, { name: 'sendInvitations', checked: event.target.checked })} />}
            label="Send me testing invitations"
            sx={{ alignSelf: 'center' }}
          />
        </FormGrid>

        <NextButton onClick={this.onSave} busy={userSaveProcessing} disabled={errors.size > 0} />
      </>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Personal));
